import React, { useState, useEffect } from 'react';
import { 
  getUseCaseById, 
  createUseCase, 
  updateUseCase, 
  runLLM,
  getTaxonomyByUseCase,
  generateCustomerMail,
  getCustomerMailStatus
} from '../services/api';
import GeneratedOutput from './GeneratedOutput';
import TaxonomyPanel from './TaxonomyPanel';

const UseCaseContainer = ({
  useCaseId,
  refreshUseCaseList,
  onSelectNewlyCreated,
}) => {
  // Initialize useCase with a default structure matching your data
  const [useCase, setUseCase] = useState({
    id: '',
    name: '',
    prompt: '',
    comments: '',
    created_at: '',
    updated_at: ''
  });
  const [name, setName] = useState('');
  const [comments, setComments] = useState('');
  
  // Update to handle 3 separate prompts
  const [systemPrompt, setSystemPrompt] = useState('');
  const [taskPrompt, setTaskPrompt] = useState('');
  const [additionalPrompt, setAdditionalPrompt] = useState('');
  
  const [customerData, setCustomerData] = useState('');
  const [taxonomy, setTaxonomy] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [generatedOutput, setGeneratedOutput] = useState('');
  const [jobId, setJobId] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [jobStatus, setJobStatus] = useState(null);
  const [pollingAttempts, setPollingAttempts] = useState(0);
  const [taxonomyText, setTaxonomyText] = useState(''); // Add this missing state

  // Add useEffect to clean up state when component unmounts or useCaseId changes
  useEffect(() => {
    // Reset generation state when switching use cases
    setGeneratedOutput('');
    setJobStatus(null);
    setIsPolling(false);
    setPollingAttempts(0);
    setJobId(null);
  }, [useCaseId]);

  // Add useEffect to load use case data
  useEffect(() => {
    loadUseCase();
  }, [useCaseId]); // Reload when useCaseId changes

  useEffect(() => {
    if (useCaseId && useCaseId !== 'new') {
      loadTaxonomy();
    } else if (useCaseId === 'new') {
      setUseCase({ id: '', name: '', prompt: '' });
      setCustomerData('');
      setTaxonomy(null);
      loadCustomerTemplate();
    }
  }, [useCaseId]);

  const loadUseCase = async () => {
    if (!useCaseId || useCaseId === 'new') {
      // For new use cases, reset everything to empty
      setUseCase({ name: '', prompt: '', comments: '' });
      setName('');
      setComments('');
      setSystemPrompt('');
      setTaskPrompt('');
      setAdditionalPrompt('');
      return;
    }
    
    try {
      setIsLoading(true);
      const response = await getUseCaseById(useCaseId);
      console.log('Loaded use case:', response);
      
      if (response && response.data) {
        const useCaseData = response.data;
        
        setUseCase(useCaseData);
        setName(useCaseData.name || '');
        setComments(useCaseData.comments || '');
        
        // Handle both old format (single prompt string) and new format (prompt object)
        if (typeof useCaseData.prompt === 'object' && useCaseData.prompt !== null) {
          // New format - extract the 3 prompts
          setSystemPrompt(useCaseData.prompt.system_prompt || '');
          setTaskPrompt(useCaseData.prompt.task_prompt || '');
          setAdditionalPrompt(useCaseData.prompt.additional_prompt || '');
        } else if (typeof useCaseData.prompt === 'string') {
          // Old format - put the string in task_prompt
          setSystemPrompt('');
          setTaskPrompt(useCaseData.prompt || '');
          setAdditionalPrompt('');
        } else {
          // Default empty prompts
          setSystemPrompt('');
          setTaskPrompt('');
          setAdditionalPrompt('');
        }
        
        // Load associated taxonomy
        await loadTaxonomy();
      }
    } catch (error) {
      console.error('Error loading use case:', error);
      alert('Fehler beim Laden des Use Cases');
    } finally {
      setIsLoading(false);
    }
  };

  const loadTaxonomy = async () => {
    if (!useCaseId) {
      setTaxonomyText('');
      setTaxonomy(null); // Make sure to clear taxonomy data
      return;
    }

    try {
      const data = await getTaxonomyByUseCase(useCaseId);
      console.log('Loaded taxonomy data for', useCaseId, ':', data);
      
      if (data) {
        setTaxonomy(data); // Set the taxonomy data object
        setTaxonomyText(JSON.stringify(data, null, 2));
      } else {
        setTaxonomy(null);
        setTaxonomyText('Keine Taxonomie für diesen UseCase gefunden.');
      }
    } catch (error) {
      console.error('Error loading taxonomy:', error);
      setTaxonomy(null);
      setTaxonomyText('Fehler beim Laden der Taxonomie.');
    }
  };

  const loadCustomerTemplate = async () => {
    try {
      // Use the corrected structure from test_customer_data.json
      const template = {
        "id": "1",
        "pflegesituation": {
          "weiterePersonen": false,
          "ort": "Frankfurt",
          "betreuungsbeginn": "2017-06-27",
          "plz": "60435",
          "anzahlPatienten": 1,
          "strasse": "Kurzröderstraße 50"
        },
        "anfragesteller": {
          "kundeIstAnsprechpartner": false,
          "ort": "Frankfurt a.M.",
          "plz": "60435",
          "telefon": "069-549699",
          "anrede": "frau",
          "vorname": "Elisabeth",
          "nachname": "Weyrauch",
          "strasse": "Kurzröderstraße 50",
          "email": "kapp@fta-tax.de",
          "weiteresTelefon": "",
          "weitereEmail": "",
          "fax": "0179 6820113 Hr Kapp"
        },
        "patienten": [
          {
            "nachtruhe": {
              "nachteinsaetze": "dreimal"
            },
            "index": 1,
            "demenz": {
              "patientDement": true
            },
            "personendaten": {
              "telefon": "069-549699",
              "email": "kapp@fta-tax.de",
              "vorname": "Elisabeth",
              "nachname": "Weyrauch",
              "gewicht": 85,
              "geschlecht": "female",
              "pflegegrad": {
                "pflegegrad": "fuenf"
              },
              "geburtstag": "1944-02-10"
            },
            "taetigkeiten": {
              "mobilisierung": [],
              "stehenGehenBetten": [],
              "koerperpflege": ["morgens"],
              "inkontinenzMaterialWechsel": [],
              "intimpflege": ["morgens"],
              "duschen": [],
              "toilettengang": ["morgens", "abends"],
              "ankleiden": ["morgens", "abends"],
              "kochen": [],
              "nahrungsaufnahme": []
            },
            "krankheiten": {
              "krankheiten": ["inkontinenz", "katheter"],
              "inkontinenz": {},
              "weitereDetails": "Alzheimer-Demenz im mittleren Stadium"
            },
            "mobilitaet": {
              "transfer": {
                "transferSchlafWohnzimmer": "selbststaendig",
                "hilfsmittel": false,
                "weiterePerson": false,
                "begleitung": false
              },
              "mobilitaet": "bettlaegerig"
            },
            "kommunikation": {},
            "weitereAngaben": {
              "gewohnheiten": {}
            },
            "nahrungsaufnahme": {},
            "pflegedienst": {
              "pflegedienstBeauftragt": true,
              "aufgaben": {}
            }
          }
        ],
        "unterbringung": {
          "eigenerFernseher": false,
          "internet": "wirdEingerichtet",
          "eigenesBad": true,
          "uebergabe": true,
          "uebergabeSchlafplatz": true,
          "uebergabeTage": "drei",
          "hausbesuch": true,
          "hausbesuchWann": "2024-11-27"
        },
        "wohnungUndHaushalt": {
          "wohngegend": "stadt",
          "wohnverhaeltnisse": "haus"
        },
        "taetigkeitenImHaushalt": {
          "taetigkeiten": ["weitere"],
          "weitereDetails": "Aufräumen, Bügeln, Einkaufen, Fenster putzen, Kochen, Sauber machen, Speisenplanung, Wäsche waschen, Versorgung der Haustiere",
          "werKauftEin": "pflegekraft",
          "wochenbudget": 80,
          "geschaefteErreichbar": "zuFuss"
        },
        "entlastung": {},
        "anforderungen": {
          "fuehrerscheinNotwendig": true,
          "sprachkenntnisse": "mittelstufe",
          "geschlechtPflegekraft": "weiblich",
          "erfahrung": "fortgeschritten",
          "raucher": "nurDraussen",
          "fokus": {
            "haushalt": 3,
            "betreuung": 4,
            "pflege": 3
          },
          "getriebe": "schaltung",
          "autoMarkeModell": "Audi A4",
          "autoWohinWieOft": "test"
        }
      };
      setCustomerData(JSON.stringify(template, null, 2));
    } catch (error) {
      console.error('Error loading customer template:', error);
    }
  };

  const handleChange = (field, value) => {
    if (field === 'prompt') {
      setUseCase(prev => ({ ...prev, prompt: value }));
    } else if (field === 'name') {
      setUseCase(prev => ({ ...prev, name: value }));
    }
  };

  const handleRun = async () => {
    if (!customerData.trim()) {
      alert('Bitte laden Sie zuerst ein Kunden-Template oder geben Sie Kundendaten ein.');
      return;
    }

    if (!useCase.id && useCaseId === 'new') {
      alert('Please save the use case first before running it.');
      return;
    }

    try {
      setIsLoading(true);
      setGeneratedOutput('');
      setJobStatus('starting');
      setPollingAttempts(0);
      setIsPolling(false);
      
      if (customerData.trim()) {
        let parsedCustomerData;
        try {
          parsedCustomerData = JSON.parse(customerData);
          console.log('Using customer mail API with data:', parsedCustomerData);
          
          const response = await generateCustomerMail(useCase.id || useCaseId, parsedCustomerData);
          
          if (response.job_id) {
            setJobId(response.job_id);
            setJobStatus(response.status || 'pending');
            setIsPolling(true);
            pollJobStatus(response.job_id);
          } else {
            setGeneratedOutput('Error: No job ID received from server');
            setJobStatus('error');
          }
          
        } catch (parseError) {
          alert('Invalid JSON format in customer data. Please check the syntax.');
          setJobStatus(null);
          return;
        }
      } else {
        const response = await runLLM(useCase.id || useCaseId, useCase.prompt);
        if (response.success) {
          setGeneratedOutput(response.message);
          setJobStatus('completed');
        } else {
          setGeneratedOutput(`Error: ${response.message}`);
          setJobStatus('error');
        }
      }
      
    } catch (error) {
      console.error('Error running use case:', error);
      setGeneratedOutput(`Error: ${error.message}`);
      setJobStatus('error');
    } finally {
      setIsLoading(false);
    }
  };

  const pollJobStatus = async (jobId, maxAttempts = 50) => {
    let attempts = 0;
    
    const poll = async () => {
      try {
        attempts++;
        setPollingAttempts(attempts);
        const response = await getCustomerMailStatus(jobId);
        console.log('Job status:', response);
        
        setJobStatus(response.status);
        
        if (response.status === 'completed') {
          setGeneratedOutput(response.email_text || 'Email generated successfully but content is empty.');
          setIsPolling(false);
          setJobStatus('completed');
          return;
        }
        
        if (response.status === 'failed') {
          const errorMsg = response.error || 'Unknown error occurred during processing';
          setGeneratedOutput(`Error: Job failed - ${errorMsg}`);
          setIsPolling(false);
          setJobStatus('failed');
          return;
        }
        
        // Handle both 'pending' and 'processing' statuses
        if (attempts < maxAttempts && (response.status === 'pending' || response.status === 'processing')) {
          // Increase interval for background processing (3 seconds instead of 2)
          setTimeout(poll, 3000);
        } else if (attempts >= maxAttempts) {
          setGeneratedOutput(`Timeout: Job took too long to complete after ${maxAttempts} attempts (${maxAttempts * 3} seconds). The job may still be processing in the background.`);
          setIsPolling(false);
          setJobStatus('timeout');
        }
        
      } catch (error) {
        console.error('Error polling job status:', error);
        setGeneratedOutput(`Polling error: ${error.message}`);
        setIsPolling(false);
        setJobStatus('error');
      }
    };
    
    poll();
  };

  const handleSave = async () => {
    if (!name.trim()) {
      alert('Bitte geben Sie einen Namen für den Use Case ein.');
      return;
    }

    try {
      setIsSaving(true);
      
      // Ensure all prompt fields are included, even if empty
      const useCaseData = {
        name: name.trim(),
        system_prompt: systemPrompt || '', // Ensure it's never undefined
        task_prompt: taskPrompt || '',     // Ensure it's never undefined  
        additional_prompt: additionalPrompt || '', // Ensure it's never undefined
        comments: comments.trim()
      };

      console.log('Saving use case data:', useCaseData); // Debug log

      let result;
      if (useCaseId && useCaseId !== 'new') {
        result = await updateUseCase(useCaseId, useCaseData);
      } else {
        result = await createUseCase(useCaseData);
        if (result && result.data && result.data.id) {
          onSelectNewlyCreated(result.data.id);
        }
      }

      if (result && result.success) {
        alert(useCaseId && useCaseId !== 'new' ? 'Use Case erfolgreich aktualisiert!' : 'Use Case erfolgreich erstellt!');
        if (refreshUseCaseList) {
          refreshUseCaseList();
        }
      }
    } catch (error) {
      console.error('Error saving use case:', error);
      alert('Fehler beim Speichern des Use Cases: ' + (error.message || error));
    } finally {
      setIsSaving(false);
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <div>Lade Use Case...</div>
      </div>
    );
  }

  // Show message if no use case is selected
  if (!useCaseId) {
    return (
      <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
        <div>Bitte wählen Sie einen Use Case aus der Liste aus oder erstellen Sie einen neuen.</div>
      </div>
    );
  }

  // Format the display name with ID
  const displayName = useCase.name && useCase.id ? `${useCase.name} - ${useCase.id}` : useCase.name || 'New Use Case';

  const getStatusDisplay = () => {
    if (!jobStatus) return null;
    
    const statusConfig = {
      starting: {
        emoji: '🚀',
        text: 'Initializing job...',
        color: '#2196f3'
      },
      pending: { 
        emoji: '⏳', 
        text: 'Job created and waiting to start...',
        color: '#ffa726' 
      },
      processing: { 
        emoji: '🔄', 
        text: 'Processing in background...',
        color: '#42a5f5' 
      },
      completed: {
        emoji: '✅',
        text: 'Email generated successfully!',
        color: '#4caf50'
      },
      failed: { 
        emoji: '❌', 
        text: 'Job failed',
        color: '#ef5350' 
      },
      timeout: { 
        emoji: '⏰', 
        text: 'Polling timeout',
        color: '#ff9800' 
      },
      error: { 
        emoji: '🚨', 
        text: 'Polling error',
        color: '#f44336' 
      }
    };
    
    const config = statusConfig[jobStatus] || { emoji: '🔄', text: 'Processing...', color: '#42a5f5' };
    
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 12px',
        backgroundColor: `${config.color}20`,
        border: `1px solid ${config.color}60`,
        borderRadius: '6px',
        marginTop: '8px',
        fontSize: '14px',
        color: config.color
      }}>
        <span style={{ fontSize: '16px' }}>{config.emoji}</span>
        <span>{config.text}</span>
        {isPolling && (
          <span style={{ marginLeft: 'auto', fontSize: '12px', opacity: 0.8 }}>
            Attempt {pollingAttempts}/50
          </span>
        )}
      </div>
    );
  };

  return (
    <div style={{ 
      padding: "20px", 
      backgroundColor: "#f9f9f9", 
      minHeight: "100vh", 
      overflow: "auto" // Keep this for the main container
    }}>
      <div style={{ marginBottom: "20px" }}>
        <h2 style={{ color: "var(--primary-coral)", marginBottom: "10px" }}>
          {useCaseId === 'new' ? '🆕 Neuer Use Case' : `📝 Use Case bearbeiten`}
        </h2>
        {useCaseId !== 'new' && useCase && (
          <p style={{ color: "#666", margin: "0" }}>
            ID: {useCaseId} {useCase.name ? `- ${useCase.name}` : ''}
          </p>
        )}
      </div>
      
      <div style={{ 
        display: "flex", 
        gap: "30px", 
        minHeight: "calc(100vh - 140px)",
        maxHeight: "calc(100vh - 140px)",
        overflow: "hidden"
      }}>
        <div style={{ 
          flex: "1", // This will be 1/3 of the width
          minHeight: "100%", 
          overflow: "auto",
          paddingRight: "10px"
        }}>
          {/* Name */}
          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
              Name:
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{
                width: "100%",
                padding: "12px",
                border: "1px solid #ddd",
                borderRadius: "6px",
                fontSize: "14px",
                boxSizing: "border-box"
              }}
              placeholder="Use Case Name eingeben..."
            />
          </div>

          {/* System Prompt */}
          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
              System Prompt (Konditionierung des LLMs):
            </label>
            <textarea
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              style={{
                width: "100%",
                height: "80px",
                padding: "12px",
                border: "1px solid #ddd",
                borderRadius: "6px",
                fontSize: "14px",
                resize: "none",
                boxSizing: "border-box"
              }}
              placeholder="System-Level Anweisungen für die KI..."
            />
          </div>

          {/* Task Prompt */}
          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
              Task Prompt (Beschreibung der Aufgabe):
            </label>
            <textarea
              value={taskPrompt}
              onChange={(e) => setTaskPrompt(e.target.value)}
              style={{
                width: "100%",
                height: "80px",
                padding: "12px",
                border: "1px solid #ddd",
                borderRadius: "6px",
                fontSize: "14px",
                resize: "none",
                boxSizing: "border-box"
              }}
              placeholder="Hauptaufgabe und Anweisungen..."
            />
          </div>

          {/* Additional Prompt */}
          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
              Additional Prompt (Beschreibung vom Email-Format):
            </label>
            <textarea
              value={additionalPrompt}
              onChange={(e) => setAdditionalPrompt(e.target.value)}
              style={{
                width: "100%",
                height: "80px",
                padding: "12px",
                border: "1px solid #ddd",
                borderRadius: "6px",
                fontSize: "14px",
                resize: "none",
                boxSizing: "border-box"
              }}
              placeholder="Zusätzliche Anweisungen oder Kontext..."
            />
          </div>

          {/* SAVE BUTTON - ALWAYS VISIBLE */}
          <div style={{ marginTop: "20px", marginBottom: "30px" }}>
            <button
              onClick={handleSave}
              disabled={isSaving}
              style={{
                backgroundColor: isSaving ? "#ccc" : "var(--primary-coral)",
                color: "white",
                border: "none",
                padding: "15px 30px",
                borderRadius: "6px",
                fontSize: "16px",
                fontWeight: "bold",
                cursor: isSaving ? "not-allowed" : "pointer",
                width: "100%",
                boxSizing: "border-box"
              }}
            >
              {isSaving ? "Speichern..." : (useCaseId === 'new' ? 'Use Case erstellen' : 'Änderungen speichern')}
            </button>
          </div>
        </div>

        {/* Right Panel - Test & Results */}
        <div style={{ 
          flex: "2", // This will be 2/3 of the width
          minHeight: "100%", 
          display: "flex", 
          flexDirection: "column",
          overflow: "auto",
          paddingLeft: "10px",
          paddingBottom: "30px" // Add bottom padding
        }}>
          {/* Customer Template Section - Smaller */}
          <div style={{ marginBottom: "10px", flexShrink: 0 }}>
            <h3 style={{ marginBottom: "8px", color: "var(--primary-coral)", fontSize: "16px" }}>
              📋 Kunden-Template
            </h3>
            <button
              onClick={loadCustomerTemplate}
              disabled={isLoading}
              style={{
                backgroundColor: isLoading ? "#ccc" : "#4CAF50",
                color: "white",
                border: "none",
                padding: "6px 12px",
                borderRadius: "4px",
                fontSize: "11px",
                cursor: isLoading ? "not-allowed" : "pointer",
                marginBottom: "6px"
              }}
            >
              {isLoading ? "Lade Template..." : "Template laden"}
            </button>
            
            <textarea
              value={customerData}
              onChange={(e) => setCustomerData(e.target.value)}
              style={{
                width: "100%",
                height: "100px", // Reduced from 120px
                padding: "6px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                fontSize: "10px",
                fontFamily: "monospace",
                resize: "vertical",
                boxSizing: "border-box"
              }}
              placeholder="Kundendaten als JSON hier eingeben oder Template laden..."
            />
          </div>

          {/* Generate Button */}
          <div style={{ marginBottom: "10px", flexShrink: 0 }}>
            <button
              onClick={handleRun}
              disabled={isPolling || isLoading || !customerData.trim()}
              style={{
                backgroundColor: (isPolling || isLoading) ? "#ccc" : "var(--primary-coral)",
                color: "white",
                border: "none",
                padding: "10px 20px",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "500",
                cursor: (isPolling || isLoading || !customerData.trim()) ? "not-allowed" : "pointer",
                width: "100%"
              }}
            >
              {isPolling ? `Generating... (${pollingAttempts}/50)` : 
               isLoading ? 'Starting...' : 
               "E-Mail generieren"}
            </button>
            
            {getStatusDisplay()}
          </div>

          {/* Taxonomy Section - Compact with Collapsible */}
          <div style={{ marginBottom: "10px", flexShrink: 0 }}>
            <h3 style={{ marginBottom: "8px", color: "var(--primary-coral)", fontSize: "16px" }}>
              🗂 Taxonomie
            </h3>
            <div style={{ 
              maxHeight: "150px", // Increased from 80px
              overflow: "auto",
              border: "1px solid #ddd",
              borderRadius: "4px",
              padding: "6px",
              fontSize: "11px",
              backgroundColor: "#f9f9f9"
            }}>
              <TaxonomyPanel taxonomyText={taxonomyText} jsonData={taxonomy} />
            </div>
          </div>

          {/* GENERATED EMAIL - Takes remaining space and shows properly */}
          {generatedOutput && (
            <div style={{ 
              flex: "1", 
              display: "flex", 
              flexDirection: "column", 
              minHeight: "400px",
              maxHeight: "400px", // Add this to constrain the height
              marginBottom: "30px" // Add margin bottom for spacing
            }}>
              <h3 style={{ 
                marginBottom: "8px", 
                color: "var(--primary-coral)", 
                flexShrink: 0,
                fontSize: "16px" 
              }}>
                📧 Generated Email
              </h3>
              <div style={{
                flex: "1",
                border: "1px solid #ddd",
                borderRadius: "6px",
                backgroundColor: "white",
                overflow: "hidden", // Change from "auto" to "hidden" on container
                minHeight: "350px",
                maxHeight: "350px", // Add this to ensure consistent height
                display: "flex",
                flexDirection: "column"
              }}>
                <GeneratedOutput text={generatedOutput} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  function getPollingButtonText() {
    if (jobStatus === 'pending') return 'Job Pending...';
    if (jobStatus === 'processing') return 'Processing...';
    return 'Generating...';
  }
};

export default UseCaseContainer;

